import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { LogoMarkStatic } from "@/components/LogoMark";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", {
        email,
        app_origin: window.location.origin,
      });
      setSent(true);
      toast.success("Check your email for the reset link");
    } catch {
      // We always show success to avoid email enumeration
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05070A] text-slate-100 relative overflow-hidden flex items-center justify-center px-6">
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[300px] w-[600px] rounded-full bg-emerald-500/10 blur-[120px]" />

      <div className="relative z-10 w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>

        <div className="ae-card p-8">
          <div className="flex items-center justify-center mb-6">
            <LogoMarkStatic className="h-20 md:h-[144px]" />
          </div>

          {sent ? (
            <div className="text-center py-6" data-testid="reset-sent">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
              <h2 className="font-heading text-2xl tracking-tight">Check your inbox</h2>
              <p className="text-sm text-slate-400 mt-3">
                If <span className="text-slate-200 font-mono">{email}</span> is registered, we have sent a password reset link. The link expires in 1 hour.
              </p>
              <Link to="/login" className="btn-ghost text-sm mt-6 inline-block">Back to sign in</Link>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-widest text-emerald-400 mb-2">Forgot password</div>
              <h1 className="font-heading text-3xl tracking-tight">Reset your password.</h1>
              <p className="text-sm text-slate-400 mt-1">We will email you a secure link.</p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Email</label>
                  <input
                    data-testid="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/60"
                  />
                </div>
                <button type="submit" disabled={busy} data-testid="forgot-submit" className="btn-primary w-full inline-flex items-center justify-center gap-2">
                  <Mail className="h-4 w-4" /> {busy ? "Sending…" : "Send reset link"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
